import LocalImages from "assets/images/local-images";
import Content, { ContentNS } from "content/content";
import { getPaxSessionRelativeUrl } from "features/shared/hooks/navigation/useFlowRedirect";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import Body from "features/shared/styles/typography/Body.styled";
import { MicroHeader } from "features/shared/styles/typography/MicroHeader.styled";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import { PaxSessionId } from "models/session/pax-session";
import { FC } from "react";
import { useAppStore } from "store/store";
import { normalizeName } from "utils";

import PaxAction from "./styles/PaxAction.styled";
import PaxInfo from "./styles/SelectPassengerPaxInfo.styled";
import PaxInfoContainer from "./styles/PaxInfoContainer.styled";
import PaxItem from "./styles/PaxItem.styled";
// @ts-expect-error - This is how we utilize dynamic imports with WebPack NormalModuleReplacementPlugin
import SelectPassengerCard from "./styles/SelectPassengerCard-APP_CLIENT.styled";

const SelectPassenger: FC = () => {
  const navigate = useNavigateWithQuery();

  const text = (s: string) => Content.shared.t(s, { ns: ContentNS.checklist });

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSessions = useAppStore((state) => state.paxSessions);

  const images = LocalImages.shared.images;
  const getIconForStatus = (status: ChecklistResolutionStatus) => {
    switch (status) {
      case ChecklistResolutionStatus.ReadyToFly:
        return images.icons.session.resolved;
      case ChecklistResolutionStatus.EntryNotAllowed:
        return images.icons.session.hasIssues;
      default:
        return images.icons.session.actionRequired;
    }
  };

  const calculateAge = (birthdate?: Date | null): number | null => {
    if (!birthdate) return null;
    const today = new Date();
    const age = today.getFullYear() - birthdate.getFullYear();
    const monthDiff = today.getMonth() - birthdate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthdate.getDate())
    ) {
      return age - 1;
    }
    return age;
  };

  const onItemClick = (paxSessionId: PaxSessionId) => {
    navigate(`${getPaxSessionRelativeUrl(sessionId, paxSessionId)}/checklist`);
  };

  return (
    <SelectPassengerCard>
      {paxSessions.map((session, i) => {
        const resolutionStatus = session.checklist.resolutionStatus;

        const pax = session.pax;
        const paxName = `${pax.firstName || ""} ${pax.surname || ""}`.trim();
        const age = calculateAge(pax.birthdate);

        const duplicateNames = paxSessions.filter(
          (s) =>
            `${s.pax.firstName || ""} ${s.pax.surname || ""}`.trim() === paxName
        );

        const normalisedPaxName = normalizeName(paxName);

        const displayPaxName =
          duplicateNames.length > 1 && age !== null
            ? `${normalisedPaxName} (${age})`
            : normalisedPaxName;

        return (
          <PaxItem
            key={`passenger-${i}`}
            onClick={() => onItemClick(session.paxSessionId)}
          >
            <PaxInfoContainer>
              <img
                alt="resolution status"
                src={getIconForStatus(resolutionStatus)}
              />
              <PaxInfo resolutionStatus={resolutionStatus}>
                <MicroHeader>{displayPaxName}</MicroHeader>
                <Body>{text(`resolution-status.${resolutionStatus}`)}</Body>
              </PaxInfo>
            </PaxInfoContainer>
            <PaxAction status={resolutionStatus}>
              {text(`resolution-action.${resolutionStatus}`)}
            </PaxAction>
          </PaxItem>
        );
      })}
    </SelectPassengerCard>
  );
};

export default SelectPassenger;
